// ─── AnimationPreviewTile ─────────────────────────────────────────────────
// Tile usado por el catálogo de animaciones. Reemplaza el icon+label de
// antes por una previsualización en miniatura que reproduce la animación
// en loop, así el usuario ve cómo queda antes de insertarla.
//
// Detalles de implementación:
//  · La preview corre a baja resolución (480×270 / 270×480 / 360×270
//    según el aspect ratio del editor) para mantener el render barato
//    incluso cuando hay 7+ templates visibles a la vez.
//  · Cada tile maneja su propio reloj con `requestAnimationFrame`. Lo
//    hacemos así (en vez de un reloj global compartido) para poder pausar
//    automáticamente las previews que están fuera del viewport vía
//    IntersectionObserver — cuando el panel se scrollea, sólo seguimos
//    animando lo que el usuario realmente está mirando.

import React, { useEffect, useRef, useState } from 'react';
import { AnimationLayer } from './AnimationLayer';
import type { AnimationTemplate } from './types';

type Props = {
  template: AnimationTemplate<any>;
  onClick: () => void;
  /** Aspect ratio actual del editor — usamos el mismo para que la
   *  preview refleje cómo se vería en el video final. */
  aspectRatio: '16:9' | '9:16' | '4:3';
};

export const AnimationPreviewTile: React.FC<Props> = ({ template, onClick, aspectRatio }) => {
  const tileRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const [localTime, setLocalTime] = useState(0);

  // Pausamos la animación cuando el tile sale del viewport. Esto baja
  // muchísimo el costo cuando hay varios templates en la lista.
  useEffect(() => {
    const el = tileRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      entries => {
        for (const e of entries) setVisible(e.isIntersecting);
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Reloj de loop. Mientras el tile esté visible, avanzamos el playhead
  // local y damos vuelta al final del template. Si no está visible, el
  // efecto se desmonta y deja de consumir CPU.
  useEffect(() => {
    if (!visible) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      setLocalTime(t => {
        const next = t + dt;
        return next >= template.defaultDurationSec ? 0 : next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, template.defaultDurationSec]);

  // Resolución de la preview. Usamos 480 de ancho como base y derivamos
  // la altura del aspect ratio del editor.
  const previewW = aspectRatio === '9:16' ? 270 : aspectRatio === '4:3' ? 360 : 480;
  const previewH = aspectRatio === '9:16' ? 480 : aspectRatio === '4:3' ? 270 : 270;
  // Padding-top trick para mantener el aspecto en CSS sin saber el alto
  // exacto del contenedor (la grilla puede variar el ancho).
  const aspectPaddingTop = `${(previewH / previewW) * 100}%`;

  return (
    <button
      ref={tileRef}
      type="button"
      onClick={onClick}
      title={template.label}
      aria-label={template.label}
      className="group w-full text-left rounded-md bg-surface-card border border-black/[0.06] shadow-sm hover:border-brand-primary/30 hover:shadow-md hover:-translate-y-px transition-all overflow-hidden"
    >
      {/* Preview area — relación de aspecto fija, fondo neutro */}
      <div
        className="relative w-full bg-neutral-900"
        style={{ paddingTop: aspectPaddingTop }}
      >
        <div className="absolute inset-0">
          {visible ? (
            <AnimationLayer
              templateId={template.id}
              props={template.defaultProps as Record<string, unknown>}
              localTime={localTime}
              durationSec={template.defaultDurationSec}
              width={previewW}
              height={previewH}
            />
          ) : (
            // Placeholder mientras el tile aún no entró al viewport. Evita
            // montar el <Player> de Remotion antes de tiempo.
            <div className="w-full h-full" />
          )}
        </div>
      </div>
    </button>
  );
};
